import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp, ChevronDown, Briefcase } from "lucide-react";
import { categoryFor } from "../lib/categories";
import { CategoryIcon } from "../lib/categoryIcons";

const PAGE_SIZE = 20;

export default function JobList({ jobs, onJobClick }) {
    const [open, setOpen] = useState(false);

    // Mobile-only bottom sheet. Desktop no longer shows a side panel.
    return (
        <div className="md:hidden fixed left-0 right-0 bottom-0 z-[380]">
            <motion.div
                initial={{ y: "calc(100% - 68px)" }}
                animate={{ y: open ? 0 : "calc(100% - 68px)" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="mx-auto glass rounded-t-3xl shadow-2xl"
                style={{ maxHeight: "72vh" }}
                data-testid="jobs-bottom-sheet"
            >
                <button
                    className="w-full py-3 flex items-center justify-between px-5"
                    onClick={() => setOpen((o) => !o)}
                    data-testid="jobs-sheet-toggle"
                >
                    <div className="flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-[#e85812]" />
                        <span className="font-display font-semibold text-[15px] text-[#1a1e2c]">
                            {jobs.length} jobs in this area
                        </span>
                    </div>
                    {open ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                </button>
                <div
                    className="overflow-y-auto px-3 pb-6"
                    style={{ maxHeight: "calc(72vh - 68px)" }}
                >
                    <JobListRows jobs={jobs} onJobClick={onJobClick} />
                </div>
            </motion.div>
        </div>
    );
}

function JobListRows({ jobs, onJobClick }) {
    // Paginate — render PAGE_SIZE rows at a time, "Show more" reveals the next batch.
    // Reset back to the first page whenever the incoming job list changes (e.g. the
    // user pans the map or applies a category filter).
    const [visible, setVisible] = useState(PAGE_SIZE);
    useEffect(() => { setVisible(PAGE_SIZE); }, [jobs]);

    if (!jobs.length) {
        return (
            <div className="text-center text-slate-500 text-sm py-10 font-body">
                No jobs in this area. Try zooming out or clearing filters.
            </div>
        );
    }
    const shown = jobs.slice(0, visible);
    const remaining = Math.max(0, jobs.length - visible);
    return (
        <div className="flex flex-col gap-2" data-testid="job-list-rows">
            <AnimatePresence initial={false}>
                {shown.map((j) => (
                    <motion.button
                        key={j.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        onClick={() => onJobClick(j)}
                        className="w-full text-left card-soft p-3 hover:shadow-lg transition-shadow flex gap-3 items-start"
                        data-testid={`job-row-${j.id}`}
                    >
                        <JobAvatar job={j} />
                        <div className="min-w-0 flex-1">
                            <div className="font-display font-semibold text-[14.5px] text-[#1a1e2c] truncate">
                                {j.title}
                            </div>
                            <div className="text-[13px] text-slate-600 truncate">{j.company}</div>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-[12px] text-slate-500 truncate">{j.loc || j.city}</span>
                                {j.pay && (
                                    <span className="text-[12.5px] font-display font-bold text-[#e85812] shrink-0">
                                        {j.pay}
                                    </span>
                                )}
                            </div>
                        </div>
                    </motion.button>
                ))}
            </AnimatePresence>
            {remaining > 0 && (
                <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="mt-2 w-full py-2.5 rounded-2xl bg-[#fff2ea] hover:bg-[#ffdccb] text-[#e85812] font-display font-semibold text-[13px] transition-colors"
                    data-testid="job-list-show-more"
                >
                    Show {Math.min(PAGE_SIZE, remaining)} more
                    <span className="ml-1 text-[11.5px] text-[#b34309] font-medium">
                        ({visible} of {jobs.length})
                    </span>
                </button>
            )}
            {remaining === 0 && jobs.length > PAGE_SIZE && (
                <div className="text-center text-[11.5px] text-slate-400 py-2 font-body" data-testid="job-list-end">
                    You&apos;ve reached the end · {jobs.length} jobs
                </div>
            )}
        </div>
    );
}

function JobAvatar({ job }) {
    const cat = categoryFor(job.cat);
    return (
        <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: cat.color }}
        >
            <CategoryIcon catId={cat.id} size={20} />
        </div>
    );
}
